import {
  LockOutlined,
  LoginOutlined,
  LogoutOutlined,
  SafetyOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Layout, Menu, Space } from "antd";
import type { MenuProps } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import ChangePasswordModal from "./ChangePasswordModal";
import LanguageSwitcher from "./LanguageSwitcher";
import TOTPDisable from "./TOTPDisable";
import TOTPSetup from "./TOTPSetup";

const { Header } = Layout;

export default function HeaderBar(): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, currentUser, logout, refreshUser, isAdmin } = useAuth();
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState<boolean>(false);
  const [isTOTPSetupOpen, setIsTOTPSetupOpen] = useState<boolean>(false);
  const [isTOTPDisableOpen, setIsTOTPDisableOpen] = useState<boolean>(false);

  const items: MenuProps["items"] = [
    {
      key: "/",
      label: t("nav.home"),
    },
    ...(isAdmin
      ? [
          {
            key: "/users",
            label: t("nav.users"),
          },
        ]
      : []),
    {
      key: "/about",
      label: t("nav.about"),
    },
    {
      key: "/legal",
      label: t("nav.legal"),
    },
  ];

  const handleLogout = (): void => {
    logout();
    navigate("/login");
  };

  const userMenuItems: MenuProps["items"] = [
    {
      key: "profile",
      label: t("header.profile", { username: currentUser?.username, role: currentUser?.role }),
      disabled: true,
    },
    {
      type: "divider" as const,
    },
    {
      key: "change-password",
      icon: <LockOutlined />,
      label: t("header.changePassword"),
      onClick: () => setIsChangePasswordModalOpen(true),
    },
    {
      key: "2fa-settings",
      icon: <SafetyOutlined />,
      label: currentUser?.totp_enabled ? t("header.twoFaEnabled") : t("header.twoFaDisabled"),
      onClick: () => {
        if (currentUser?.totp_enabled) {
          setIsTOTPDisableOpen(true);
        } else {
          setIsTOTPSetupOpen(true);
        }
      },
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("header.logout"),
      onClick: handleLogout,
    },
  ];

  return (
    <Header
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0 24px",
      }}
    >
      <div style={{ flex: 1 }}>
        <Menu
          theme="dark"
          mode="horizontal"
          selectedKeys={[location.pathname]}
          items={items}
          onClick={({ key }) => navigate(key)}
        />
      </div>

      <Space size="large">
        <LanguageSwitcher />
        {isLoggedIn ? (
          <Dropdown menu={{ items: userMenuItems }}>
            <Button type="text" icon={<UserOutlined />} style={{ color: "white" }}>
              {currentUser?.username}
            </Button>
          </Dropdown>
        ) : (
          <Button type="primary" icon={<LoginOutlined />} onClick={() => navigate("/login")}>
            {t("header.login")}
          </Button>
        )}
      </Space>

      <ChangePasswordModal
        open={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />

      <TOTPSetup
        open={isTOTPSetupOpen}
        onClose={() => setIsTOTPSetupOpen(false)}
        onSuccess={() => {
          setIsTOTPSetupOpen(false);
          refreshUser();
        }}
      />

      <TOTPDisable
        open={isTOTPDisableOpen}
        onClose={() => setIsTOTPDisableOpen(false)}
        onSuccess={() => {
          setIsTOTPDisableOpen(false);
          refreshUser();
        }}
      />
    </Header>
  );
}
